import { useRef, forwardRef } from "react"
import { map, some, size, snakeCase, toLower, chain, get, times, each, sum, slice } from "lodash"
import PropTypes from "prop-types"
import classNames from "classnames"
import { Table as MUITable, TableCell, TableRow, TableHead, TableBody, Paper } from "@mui/material"
import { OfflineBolt, Star } from "@mui/icons-material"
import CellTooltip from "components/securities-table/cell-tooltip"
import OPTooltip from "components/material/tooltip"
import { COLUMN_GROUP } from "components/securities-table/row/shapes"
import { getCellContents } from "components/securities-table/row"
import { CONTRIBUTOR } from "helpers/shapes/simulator"
import useResizeObserver from "hooks/resize-observer"

import "./styles.scss"

const ROW_LABELS = [
  "Original",
  "Rebalanced",
  "Delta",
]

const Summary = forwardRef(({ columnGroups, data, className, isActive, onScroll, id }, ref) => {
  function getItem({ formatter, value }) {
    return typeof formatter === "function" ? formatter(value) : value
  }

  const numberOfStickyColumns = chain(columnGroups)
    .flatMap("columns")
    .filter("isSticky")
    .size()
    .value()

  const tableRef = useRef()
  // We're using useResizeObserver here because this logic needs to be executed if the table resizes, something useEffect or useLayoutEffect won't do
  useResizeObserver(tableRef, () => {
    const parentNodes = get(tableRef, "current.childNodes")
    if (!parentNodes) { return }
    // We need to find out the left positions of the first 3 columns
    // 1st: 0
    // 2+: 0 + sum(previous widths)
    // First we get the nodes of the first row with non grouped columns and get their widths
    const widths = chain(parentNodes)
      .get(0) // parentNodes contains thead and tbody, we only want thead
      .get("childNodes")
      .get(1) // TableHead contains 3 rows: Group Labels, Labels, Total and we want the Labels row
      .get("childNodes")
      .take(numberOfStickyColumns)
      // We're using getBoundingClientRect() instead of offsetWith because it gives you fractionated width values
      .map(node => node?.getBoundingClientRect().width)
      .value()
    const positions = [
      0,
      ...times(numberOfStickyColumns, (index) => sum(slice(widths, 0, index + 1))),
    ]

    each(parentNodes, parentNode => {
      const nodes = parentNode.childNodes
      each(nodes, (node) => {
        // Only iterate through columns that are sticky
        const children = node.getElementsByClassName("sticky-left")
        for (let index = 0; index < children.length; index++) {
          children[index].style.left = `${positions[index]}px`
          children[index].classList.add(`sticky-left-${index + 1}`)
          if (index === children.length - 1) {
            children[index].classList.add("sticky-left-last")
          }
        }
      })
    })
  })

  return <Paper square className="modules_rebalance_components_summary">
    <div className="components_material-table" ref={ref} onScroll={onScroll}>
      <MUITable id={id} className={className} ref={tableRef}>
        <TableHead className="header">
          <TableRow size="small" id={`${id}-categories`}>
            <TableCell size="small" className="group-end sticky-left" />
            {map(columnGroups, ({ id, category, categoryTooltip, adjustedCategoryReason, columns, color }, index) =>
              <TableCell key={`group_${index}-${id || snakeCase(category)}`} className={classNames("superheader group-start", { "sticky-left": some(columns, "isSticky") })} colSpan={size(columns)} align="center" style={{ color }}>
                {category || "\u00A0"}
                {adjustedCategoryReason && <OPTooltip title={adjustedCategoryReason}><OfflineBolt fontSize="small" className="column-adjusted-icon" /></OPTooltip>}
                {categoryTooltip && <CellTooltip contents={[categoryTooltip]} />}
              </TableCell>,
            )}
          </TableRow>
          <TableRow id={`${id}-columns`}>
            <TableCell className="group-end sticky-left" size="small" />
            {map(columnGroups, ({ category, columns }) => map(columns, (column, index) => {
              const { id, label, color, adjustedReason, isSticky, tooltip, favorite, tooltipContents } = column
              const content = typeof label === "function" ? label(isActive) : label
              return <TableCell key={`${category}${id}`} size="small" align="right" className={classNames({ "group-start": index === 0, "sticky-left": isSticky })} style={{ color }}>
                <span className="cell-content-wrapper">
                  <OPTooltip title={tooltip ?? content}><span className="ellipsable">{favorite && <Star fontSize="small" className="favorite" style={{ color }} />}{content}</span></OPTooltip>
                  {adjustedReason && <OPTooltip title={adjustedReason}><OfflineBolt fontSize="small" className="column-adjusted-icon" /></OPTooltip>}
                  {tooltipContents && <CellTooltip color={color} contents={tooltipContents} />}
                </span>
              </TableCell>
            }))}
          </TableRow>
        </TableHead>
        <TableBody>
          {map(data, (summaryRow, index) => {
            const rowLabel = ROW_LABELS[index]
            return <TableRow size="small" key={index} className={classNames("table-row", toLower(rowLabel))}>
              <TableCell className="sticky-left group-end" size="small">{rowLabel}</TableCell>
              {map(columnGroups, ({ category, columns, getter: groupGetter }) => map(columns, (column, index) => {
                const { id, isSticky, getter: columnGetter, path, formatter } = column
                const { value } = getCellContents(columnGetter ?? groupGetter, summaryRow, path || id, id)

                return <TableCell key={`${category}${id}`} size="small" align="right" className={classNames({ "group-start": index === 0, "sticky-left": isSticky })}>
                  {getItem({ formatter, value })}
                </TableCell>
              }))}
            </TableRow>
          })}
        </TableBody>
      </MUITable>
    </div>
  </Paper>
})

Summary.displayName = "Summary"

Summary.propTypes = {
  columnGroups: PropTypes.arrayOf(COLUMN_GROUP),
  data: PropTypes.arrayOf(CONTRIBUTOR),
  className: PropTypes.string,
  isActive: PropTypes.bool,
  onScroll: PropTypes.func,
  id: PropTypes.string,
}

export default Summary
